import { createAsyncThunk } from '@reduxjs/toolkit';
import { store } from '../../app/store';

// import state definition and functions (redux).
import {
  InteractiveSessionsState,
  addTab,
  setSessionRefreshing,
  setSessionsLoading,
  addSessionsLoading,
  removeSessionsLoading,
  setSessions,
  addSessions,
  removeSessions,
} from './interactive-sessions-slice';
import { DataState, rD_updateCanfarImages } from '../../app/data-slice';
import { setSelectedTool } from '../../app-slice';

// import types.
import { CanfarServiceType, ComputeServiceType, Site, ToolType } from '../../utils/types';

// import functions
import { APIPrefix } from '../../utils/functions';

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the selected
//	canfar instance.
//
//	------------------------------------------------------------

async function _getActiveSessionsCanfar(args: {
  canfarService: CanfarServiceType;
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = [];

  if (args.canfarService.url !== '') {
    //
    var urlCommand: string =
      APIPrefix() + '/v1/skaha/session?url=' + args.canfarService.url;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        // remove the current services.
        args.thunkAPI.dispatch(
          removeSessions({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['running', 'pending', 'terminating', 'failed'],
          }),
        );

        const returnedJson = await apiResult.json();

        // find this CANFAR service in the local compute array.
        const serviceIndex: number = args.stateData.computeServices.findIndex(
          (element) => element.compute_service_id === args.canfarService.id,
        );
        let healthStatus: string | undefined = undefined;
        let activeStatus: boolean = false;
        if (serviceIndex > -1) {
          healthStatus = args.stateData.computeServices[serviceIndex].health_status;
          activeStatus =
            args.stateData.computeServices[serviceIndex].active_status === true;
        }

        // is this the currently-selected site?
        for (let i: number = 0; i < returnedJson.length; i++) {
          //
          const returnedSession: {
            id: string;
            image: string;
            type: string;
            status: string;
            name: string;
            start_time: string;
            expiry_time: string;
            connect_url: string;
            ram_in_use: string;
            cpu_cores_in_use: string;
            gpu_utilization: string;
          } = returnedJson[i];

          // only include CARTA, notebook, desktop, or headless sessions.
          if (
            (returnedSession.type.toLowerCase() === 'carta' ||
              returnedSession.type.toLowerCase() === 'notebook' ||
              returnedSession.type.toLowerCase() === 'desktop' ||
              returnedSession.type.toLowerCase() === 'headless') &&
            returnedSession.status.toLowerCase() !== 'failed' &&
            returnedSession.status.toLowerCase() !== 'terminating'
          ) {
            let newService: ComputeServiceType = {
              id: -1,
              site: args.canfarService.site ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'canfar',
              session_type: returnedSession.type.toLowerCase(),
              compute_service_id: returnedSession.id,
              service_name: returnedSession.name,
              service_url: returnedSession.connect_url,
              notebook_path: '',
              started_at: returnedSession.start_time,
              expires_at: returnedSession.expiry_time,
              is_embedded: returnedSession.type.toLowerCase() !== 'notebook',
              compute_status: returnedSession.status.toLowerCase(),
              active_status: activeStatus,
              top_banner:
                '@ ' +
                (args.canfarService.name !== ''
                  ? args.canfarService.name
                  : 'unknown CANFAR instance'),
              health_status: healthStatus,
            };
            sessions.push(newService);
          }
          //
        }
        //
      } else {
        //
        // turn off the refresh flag on current services.
        args.thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            compute_status: ['running', 'pending'],
            resource_type: ['canfar'],
            refreshing: false,
          }),
        );
        //
      }
      //
    } catch (e) {}
    //
  }

  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'canfar',
        compute_status: 'running',
        id: args.canfarService.id,
      },
    ]),
  );
  //
} // _getActiveSessionsCanfar

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the SRCNet-defined
//	services.
//
//	------------------------------------------------------------

async function _getActiveSessionsSRCNet(args: { thunkAPI: any; stateData: DataState }) {
  //
  let sessions: ComputeServiceType[] = [];

  var urlCommand: string = APIPrefix() + '/v1/active_compute_sessions/grouped';

  try {
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();
      const groupedSessions: any = returnedJson['grouped_sessions'];

      // get a list of all the sites with active sessions.
      const sites: string[] = Object.keys(groupedSessions);
      for (let site of sites) {
        const services: any = groupedSessions[site];
        for (let i: number = 0; i < services.length; i++) {
          //
          // find this service in the local compute array.
          const serviceIndex: number = args.stateData.computeServices.findIndex(
            (element) => element.compute_service_id === services[i].compute_service_id,
          );
          let activeStatus: boolean | undefined = undefined;
          let activeSessionCount: number | undefined = undefined;
          let topBanner: string | undefined = undefined;
          let healthStatus: string | undefined = undefined;
          let bottomBannerType: string | undefined = undefined;
          let bottomBanner: string | undefined = undefined;
          if (serviceIndex > -1) {
            activeStatus = args.stateData.computeServices[serviceIndex]['active_status'];
            activeSessionCount =
              args.stateData.computeServices[serviceIndex]['active_session_count'];
            topBanner = args.stateData.computeServices[serviceIndex]['top_banner'];
            healthStatus = args.stateData.computeServices[serviceIndex]['health_status'];
            bottomBannerType =
              args.stateData.computeServices[serviceIndex]['bottom_banner_type'];
            bottomBanner = args.stateData.computeServices[serviceIndex]['bottom_banner'];
          }
          if (
            args.stateData.computeServices[serviceIndex].session_type === 'carta' ||
            args.stateData.computeServices[serviceIndex].session_type === 'jupyterhub'
          ) {
            let newService: ComputeServiceType = {
              id: services[i]['id'],
              site: args.stateData.sites.find((item) => item.site_uuid === site) ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'srcnet',
              session_type: args.stateData.computeServices[serviceIndex].session_type,
              compute_service_id: services[i]['compute_service_id'],
              service_name: services[i]['service_name'],
              service_url: services[i]['service_url'],
              notebook_path: services[i]['notebook_path'],
              started_at: services[i]['started_at'],
              expires_at: services[i]['expires_at'],
              is_embedded: services[i]['is_embedded'] === 1,
              compute_status: services[i]['compute_status'],
              active_status: activeStatus,
              active_session_count: activeSessionCount,
              top_banner: topBanner,
              health_status: healthStatus,
              bottom_banner_type: bottomBannerType,
              bottom_banner: bottomBanner,
            };
            sessions.push(newService);
          }
          //
        }
        //
      }
      //
    }
    //
  } catch (e) {}

  args.thunkAPI.dispatch(
    removeSessions({
      resource_type: ['srcnet'],
      compute_status: ['running', 'pending'],
    }),
  );
  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'srcnet',
        compute_status: 'running',
      },
    ]),
  );
  //
} // _getActiveSessionsSRCNet

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the selected
//	canfar instance.
//
//	------------------------------------------------------------

async function _getAvailableSessionsCanfar(args: {
  canfarService: CanfarServiceType;
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = [];

  if (args.canfarService.url !== '') {
    //
    var urlCommand: string =
      APIPrefix() + '/v1/skaha/images?url=' + args.canfarService.url;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        // remove the current services.
        args.thunkAPI.dispatch(
          removeSessions({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
          }),
        );

        const returnedJson = await apiResult.json();

        // find this CANFAR service in the local compute array.
        const serviceIndex: number = args.stateData.computeServices.findIndex(
          (element) => element.compute_service_id === args.canfarService.id,
        );
        let activeStatus: boolean = false;
        let healthStatus: string | undefined = undefined;
        if (serviceIndex > -1) {
          activeStatus =
            args.stateData.computeServices[serviceIndex].active_status === true;
          healthStatus = args.stateData.computeServices[serviceIndex].health_status;
        }

        // build a unique list of types.
        let imageTypes: string[] = [];

        var returnedImages: {
          id: string;
          types: string[];
        }[] = [];

        // loop through the returned images.
        for (let i: number = 0; i < returnedJson.length; i++) {
          //
          const returnedImage: {
            id: string;
            types: string[];
            digest?: string;
          } = Object.assign({}, returnedJson[i]);
          delete returnedImage.digest;

          // add to the array.
          returnedImages.push(returnedImage);

          // loop through the types.
          for (let j: number = 0; j < returnedImage.types.length; j++) {
            if (imageTypes.indexOf(returnedImage.types[j].toLowerCase()) === -1) {
              imageTypes.push(returnedImage.types[j].toLowerCase());
            }
          }
          //
        }

        // update the canfar instance with this list of images.
        args.thunkAPI.dispatch(
          rD_updateCanfarImages({ id: args.canfarService.id, images: returnedImages }),
        );

        // add sessions if they have the correct image type.
        const validTypes: string[] = ['CARTA', 'notebook', 'desktop', 'headless'];
        for (let i: number = 0; i < validTypes.length; i++) {
          if (imageTypes.indexOf(validTypes[i].toLowerCase()) > -1) {
            let newService: ComputeServiceType = {
              id: -1,
              site: args.canfarService.site ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'canfar',
              session_type: validTypes[i].toLowerCase(),
              compute_service_id: args.canfarService.id ?? '',
              service_name: 'Launch ' + validTypes[i] + ' session',
              service_url: '',
              is_embedded: true,
              compute_status: 'available',
              active_status: activeStatus,
              top_banner:
                '@ ' +
                (args.canfarService.name !== ''
                  ? args.canfarService.name
                  : 'unknown CANFAR instance'),
              health_status: healthStatus,
            };
            sessions.push(newService);
          }
        }
        //
      } else {
        //
        // turn off the refresh flag on current services.
        args.thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
            refreshing: false,
          }),
        );
        //
      }
      //
    } catch (e) {}
    //
  }

  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'canfar',
        compute_status: 'available',
        id: args.canfarService.id,
      },
    ]),
  );
  //
} // _getAvailableSessionsCanfar

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the SRCNet-defined
//	services.
//
//	------------------------------------------------------------

async function _getAvailableSessionsSRCNet(args: {
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = args.stateData.computeServices.filter(
    (filter) => filter.session_type === 'carta' || filter.session_type === 'jupyterhub',
  );

  args.thunkAPI.dispatch(
    removeSessions({
      resource_type: ['srcnet'],
      compute_status: ['available'],
    }),
  );
  args.thunkAPI.dispatch(addSessions(sessions));
  //
} // _getAvailableSessionsSRCNet

//	-------------------------------------------------
//
//	extract a list of suitable local computer services
//	for the given site.
//
//	-------------------------------------------------

export function getComputeServices(args: { site: Site }): ComputeServiceType[] {
  //
  const stateFetch: any = store.getState();
  const state: InteractiveSessionsState = stateFetch.interactiveSessions;

  // loop through the compute services to find items for this site.
  //return sD_computeServices.filter((filter) => {
  return state.sessions
    .filter((filter) => {
      //
      // if this service status is 'available' then check if there is a running
      // service we can show instead.
      let foundActive: boolean = false;
      if (filter.compute_status.toLowerCase() === 'available') {
        foundActive =
          state.sessions.findIndex(
            (find) =>
              find.compute_status.toLowerCase() === 'running' &&
              ((find.resource_type?.toLowerCase() === 'srcnet' &&
                find.compute_service_id === filter.compute_service_id) ||
                (find.resource_type?.toLowerCase() === 'canfar' &&
                  find.session_type === filter.session_type)) &&
              find.site.site_uuid === filter.site.site_uuid,
          ) > -1;
      }

      // only use active sites that match the site filter.
      return (
        filter.site.site_uuid === args.site?.site_uuid &&
        filter.resource_type?.toLowerCase() === 'canfar' &&
        filter.health_status?.toUpperCase() === 'ACTIVE' &&
        foundActive === false
      );
      //
    })
    .sort((a, b) => {
      //
      // sort these results to put active sessions first, and then on session type.
      let swap: boolean = false;
      if (
        a.compute_status.toLowerCase() !== 'running' &&
        b.compute_status.toLowerCase() === 'running'
      ) {
        swap = true;
      } else if (
        a.compute_status.toLowerCase() === 'running' &&
        b.compute_status.toLowerCase() !== 'running'
      ) {
        swap = false;
      } else if (a.session_type > b.session_type) {
        swap = true;
      } else if (a.session_type < b.session_type) {
        swap = false;
      } else if (a.service_name > b.service_name) {
        swap = true;
      }

      return swap ? 1 : -1;
      //
    });
  //
} // getComputeServices

//	------------------------------------------------------------
//
//	Display an active session either in a new browser tab,
//	or a new tab on the page.
//
//	------------------------------------------------------------

export function displaySession(args: { session: ComputeServiceType }) {
  //

  // open a running service, either in a new browser tab or a new tab on the page.
  if (
    (['jupyterhub', 'notebook', 'carta', 'desktop'].indexOf(args.session.session_type) >
      -1 &&
      args.session.compute_status === 'running') ||
    ['canfar', 'azimuth'].indexOf(args.session.session_type) > -1
  ) {
    //
    if (args.session.session_type === 'carta') {
      store.dispatch(addTab({ service: args.session, index: 0 }));
      store.dispatch(setSelectedTool(ToolType.VISUALISE_DATA));
    }
    if (['jupyterhub', 'notebook'].indexOf(args.session.session_type) > -1) {
      store.dispatch(addTab({ service: args.session, index: 1 }));
      store.dispatch(setSelectedTool(ToolType.NOTEBOOK));
    }
    if (args.session.session_type === 'desktop') {
      store.dispatch(addTab({ service: args.session, index: 2 }));
      store.dispatch(setSelectedTool(ToolType.VIRTUAL_DESKTOPS));
    }
    if (['canfar', 'azimuth'].indexOf(args.session.session_type) > -1) {
      store.dispatch(addTab({ service: args.session, index: -1 }));
    }
    //
  }
  //
} // displaySession

//	------------------------------------------------------------
//
//	refresh the list of CANFAR services in the side panel.
//
//	------------------------------------------------------------

export const refreshCanfarServices = createAsyncThunk(
  //
  'interactiveSessions/refreshServices',
  //
  async (
    args: {
      canfarService?: CanfarServiceType;
      refreshActive?: boolean;
      refreshAvailable?: boolean;
    },
    thunkAPI,
  ) => {
    //
    // read the state variables.
    const stateFetch: any = thunkAPI.getState();
    const state: InteractiveSessionsState = stateFetch.interactiveSessions;
    const stateData: DataState = stateFetch.data;

    // build a list of CANFAR services to check.
    var checkCanfarServices: CanfarServiceType[] = stateData.canfarServices ?? [];
    if (args.canfarService !== undefined) {
      checkCanfarServices = [args.canfarService];
    }

    for (let canfarService of checkCanfarServices) {
      //
      if (args.refreshActive !== false) {
        //
        thunkAPI.dispatch(
          addSessionsLoading([
            {
              resource_type: 'canfar',
              compute_status: 'running',
              id: canfarService.id,
            },
          ]),
        );

        // flag the current services as refreshing.
        thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [canfarService.site.site_uuid ?? '#'],
            compute_status: ['running', 'pending'],
            resource_type: ['canfar'],
            refreshing: true,
          }),
        );

        // get the active sessions from CANFAR.
        _getActiveSessionsCanfar({
          canfarService: canfarService,
          thunkAPI: thunkAPI,
          stateData: stateData,
        });
        //
      }
      if (args.refreshAvailable !== false) {
        //
        thunkAPI.dispatch(
          addSessionsLoading([
            {
              resource_type: 'canfar',
              compute_status: 'available',
              id: canfarService.id,
            },
          ]),
        );

        // flag the current services as refreshing.
        thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
            refreshing: true,
          }),
        );

        // get the available sessions from CANFAR.
        _getAvailableSessionsCanfar({
          canfarService: canfarService,
          thunkAPI: thunkAPI,
          stateData: stateData,
        });
        //
      }
      //
    }
    //
  },
  //
); // refreshCanfarServices

//	------------------------------------------------------------
//
//	refresh the list of SRCNet services in the side panel.
//
//	------------------------------------------------------------

export const refreshSRCNetServices = createAsyncThunk(
  //
  'interactiveSessions/refreshServices',
  //
  async (_, thunkAPI) => {
    //
    // read the state variables.
    const stateFetch: any = thunkAPI.getState();
    const state: InteractiveSessionsState = stateFetch.interactiveSessions;
    const stateData: DataState = stateFetch.data;

    thunkAPI.dispatch(
      setSessionsLoading([
        {
          resource_type: 'srcnet',
          compute_status: 'running',
        },
      ]),
    );

    // get the active sessions from SRCNet.
    _getActiveSessionsSRCNet({
      thunkAPI: thunkAPI,
      stateData: stateData,
    });

    if (stateData.computeServices.length > 0) {
      //
      // get the available sessions from SRCNet.
      _getAvailableSessionsSRCNet({
        thunkAPI: thunkAPI,
        stateData: stateData,
      });
      //
    } else {
      thunkAPI.dispatch(
        removeSessions({
          resource_type: ['srcnet'],
          compute_status: ['available'],
        }),
      );
    }
    //
  },
  //
); // refreshSRCNetServices
